import type { ReactNode } from 'react';
import { enableEvalsFromCode } from '@gonfalon/dogfood-flags/dogfood-flags';
import { Focusable, Tooltip, TooltipTrigger } from '@launchpad-ui/components';
import { Icon, type IconProps } from '@launchpad-ui/icons';
import cx from 'clsx';

import { getModeDescription, getModeLabel, iconNameByMode } from '../constants';
import type { AiConfigMode } from '../types';

import { AiChip } from './AiChip';

import styles from './AiModeIcon.module.css';

// Add hideIcon prop type
type HideIconProps = { hideIcon?: boolean };

// Accept hideIcon in AiModeIcon but ignore it (pass through to parent)
export function AiModeIcon({
  mode,
  hideIcon, // Swallow the prop (in case it's bubbled down)
  ...props
}: { mode: AiConfigMode } & IconProps & HideIconProps) {
  return <Icon name={iconNameByMode[mode]} {...props} />;
}

// Add hideIcon to AiModeTag
export function AiModeTag({ mode, hideIcon, ...props }: { mode: AiConfigMode } & IconProps & HideIconProps) {
  return (
    <>
      {!hideIcon && <AiModeIcon mode={mode} {...props} />}
      {getModeLabel(mode)}
    </>
  );
}

export function AiModeTooltip({ mode, children }: { mode: AiConfigMode; children: ReactNode }) {
  return (
    <TooltipTrigger>
      <Focusable>
        <span className={styles.tooltipTrigger}>{children}</span>
      </Focusable>
      <Tooltip>{getModeDescription(mode)}</Tooltip>
    </TooltipTrigger>
  );
}

// Add hideIcon to AiModeChip
export function AiModeChip({
  mode,
  className,
  hideIcon,
  ...props
}: { mode: AiConfigMode; className?: string } & IconProps & HideIconProps) {
  const size = props.size ?? 'medium';
  return enableEvalsFromCode() ? (
    <AiChip size={size} className={className}>
      <AiModeTag mode={mode} hideIcon={hideIcon} {...props} />
    </AiChip>
  ) : (
    <span className={cx(styles.chip, styles[size], className)}>
      <AiModeTag mode={mode} hideIcon={hideIcon} {...props} />
    </span>
  );
}
